"use client"
import { type CSSProperties, useRef } from "react"
import { useGSAP } from "@gsap/react"
import cn from "classnames"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

import { AnimateText } from "~/components/elements/AnimateText/AnimateText"
import { FadeIn } from "~/components/elements/FadeIn/FadeIn"
import { useScroll } from "~/components/foundations/ScrollProvider"
import { Button } from "~/components/primitives/Button/Button"
import { HtmlRenderer } from "~/components/primitives/HtmlRenderer"
import { ResponsiveAsset } from "~/components/primitives/ResponsiveAsset/ResponsiveAsset"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { OurPeopleInterface } from "./OurPeople.interface"
import s from "./OurPeople.module.scss"

gsap.registerPlugin(ScrollTrigger)

export type OurPeopleViewProps = Omit<OurPeopleInterface, "id"> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
}

export const OurPeopleView = ({
  heading,
  body,
  cta,
  primaryImages,
  backgroundImages,
  config,
  moduleIndex,
  className,
  style,
}: OurPeopleViewProps) => {
  const configProps = getModuleConfigProps(config)
  const { lenis } = useScroll()

  const primaryAssets = primaryImages.slice(0, 3) ?? []

  const hasBackgroundImages = backgroundImages.length > 0

  const sectionRef = useRef<HTMLElement>(null)
  const primaryImageRefs = useRef<(HTMLDivElement | null)[]>([])
  const backgroundImageRefs = useRef<(HTMLDivElement | null)[]>([])

  useGSAP(
    () => {
      if (!lenis) return

      const mm = gsap.matchMedia()

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const scrollTrigger = {
          trigger: sectionRef.current,
          scroller: lenis.rootElement,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        }

        primaryImageRefs.current.forEach((ref, index) => {
          if (!ref) return

          const speeds = [60, 160, 100]

          const speed = speeds[index] ?? 0

          gsap.fromTo(
            ref,
            { y: speed },
            {
              y: -speed,
              ease: "none",
              scrollTrigger,
            },
          )
        })

        backgroundImageRefs.current.forEach((ref) => {
          if (!ref) return

          gsap.fromTo(
            ref,
            { y: 60 },
            {
              y: -60,
              ease: "none",
              scrollTrigger,
            },
          )
        })
      })
    },
    { scope: sectionRef, dependencies: [lenis] },
  )

  return (
    <section
      ref={sectionRef}
      className={cn(s.ourPeople, `stack-module-${moduleIndex}`, className)}
      data-module="our-people"
      {...configProps}
      style={{ ...configProps.style, ...style }}
    >
      <div className={cn(s.inner, "container", "default-grid")}>
        {/* Background blurred images — lowest z-index */}
        {hasBackgroundImages && (
          <div className={s.backgroundLayer} aria-hidden="true">
            {backgroundImages.map((image, index) => (
              <div
                key={`bg-${index}`}
                ref={(el) => {
                  backgroundImageRefs.current[index] = el
                }}
                style={
                  {
                    "--scale": index === 0 ? 1.3 : 1,
                    "--blur-scale": index === 0 ? "15px" : "20px",
                  } as CSSProperties
                }
                className={cn(s.backgroundImage, s[`bgPosition${index + 1}`])}
              >
                <ResponsiveAsset
                  desktop={image}
                  imageSizes={{ base: "30vw", md: "20vw" }}
                  loading="lazy"
                  animateOnLoad={false}
                  imageProps={{ alt: "" }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Primary image 1 */}
        <div
          key={`primary-1`}
          ref={(el) => {
            primaryImageRefs.current[0] = el
          }}
          className={cn(s.primaryImage, s[`position1`])}
        >
          <div className={s.primaryImageContainer}>
            <ResponsiveAsset
              desktop={primaryAssets[0]}
              imageSizes={{ base: "45vw", md: "25vw", lg: "20vw" }}
              loading="lazy"
              animateOnLoad={false}
            />
          </div>
        </div>

        {/* Text content — centered */}
        <div className={s.textContent}>
          {heading && (
            <FadeIn>
              <AnimateText element="h2" className={s.heading}>
                <HtmlRenderer html={heading} />
              </AnimateText>
            </FadeIn>
          )}
          {body && (
            <HtmlRenderer
              animate
              html={body}
              element="div"
              className={s.body}
              delay={0.1}
            />
          )}
          {cta && (
            <FadeIn delay={0.2}>
              <Button
                cta={{ ...cta, variant: "tertiary" }}
                theme={configProps["data-typography"] || "light"}
                className={s.cta}
              />
            </FadeIn>
          )}
        </div>

        {/* Primary assets 2 & 3 */}
        {primaryAssets.slice(1, 3).map((image, index) => (
          <div
            key={`primary-${index + 1}`}
            ref={(el) => {
              primaryImageRefs.current[index + 1] = el
            }}
            className={cn(s.primaryImage, s[`position${index + 2}`])}
          >
            <div className={s.primaryImageContainer}>
              <ResponsiveAsset
                desktop={image}
                imageSizes={{ base: "45vw", md: "25vw", lg: "20vw" }}
                loading="lazy"
                animateOnLoad={false}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
